import config, { setConfig } from "@/config";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { APP_ROOT } from "./env.js";

type FetchConfigResponse = {
  ok: boolean;
  status: number;
  text: string;
};

const CONFIG_REQUEST_HEADERS = {
  "Cache-Control": "no-store",
};
const MAX_CONFIG_REDIRECTS = 20;

async function fetchConfig(url: string): Promise<FetchConfigResponse> {
  if (typeof fetch === "function") {
    const res = await fetch(url, {
      headers: CONFIG_REQUEST_HEADERS,
      cache: "no-store",
    });
    return {
      ok: res.ok,
      status: res.status,
      text: await res.text(),
    };
  }

  return fetchConfigWithNode(url, MAX_CONFIG_REDIRECTS);
}

async function fetchConfigWithNode(url: string, redirectsLeft: number): Promise<FetchConfigResponse> {
  const [http, https] = await Promise.all([import("http"), import("https")]);
  const client = url.startsWith("https") ? https : http;

  return new Promise((resolve, reject) => {
    const req = client.get(url, { headers: CONFIG_REQUEST_HEADERS }, res => {
      if (
        res.statusCode &&
        res.statusCode >= 300 &&
        res.statusCode < 400 &&
        res.headers.location &&
        redirectsLeft > 0
      ) {
        res.resume();
        const redirectUrl = new URL(res.headers.location, url).toString();
        fetchConfigWithNode(redirectUrl, redirectsLeft - 1).then(resolve, reject);
        return;
      }

      const chunks: Buffer[] = [];
      res.on("data", chunk => chunks.push(Buffer.isBuffer(chunk) ? chunk : Buffer.from(chunk)));
      res.on("error", reject);
      res.on("end", () => {
        const status = res.statusCode || 0;
        resolve({
          ok: status >= 200 && status < 300,
          status,
          text: Buffer.concat(chunks).toString("utf-8"),
        });
      });
    });

    req.on("error", reject);
  });
}

async function loadConfig(url: string): Promise<any> {
  let error: Error;
  let res: FetchConfigResponse | undefined = undefined;
  let responseBody = "";

  if (process.env.VUE_APP_TARGET_ENV === "server" && !url.startsWith("http")) {
    const fs = await import("fs");
    const path = await import("path");
    return JSON.parse(await fs.promises.readFile(path.resolve(__dirname, APP_ROOT + url), "utf-8"));
  }

  try {
    res = await fetchConfig(url);
    responseBody = res.text;
    if (!res.ok) {
      throw new Error(`Request failed with status code ${res.status}`);
    }
    return JSON.parse(responseBody);
  } catch (e) {
    let errorMsg = `Error loading config from ${url}: ${(e as Error).message}`;
    if (res) errorMsg += `\n status: ${res.status} \n body response: ${responseBody.substr(0, 500).replace(/\n/g, "")}`;
    error = new Error(errorMsg);
  }
  return Promise.reject(error);
}

export default function (devConfig?: Record<string, string>) {
  if (devConfig) {
    setConfig(devConfig);
  }
  return Promise.all([
    loadConfig("/project/js/config.json"),
    loadConfig(`${config.backendUrl}/system-settings/frontend-config`),
  ]).then(configs => {
    configs.forEach(c => setConfig(c));
    if (process.env.NODE_ENV === "development") {
      const features = localStorage.getItem("features");
      if (features) {
        setConfig(JSON.parse(features));
      }
    }
  });
}
